import React, { useState } from 'react'
import {
  Button,
  IconButton
} from '@contrast-security-inc/design-system-buttons'
import { useIntl } from 'react-intl'
import {
  theme,
  IconPositiveResults,
  IconNegativeResults,
  IconAi
} from '@contrast-security-inc/design-system-foundations'
import { Spinner } from '@contrast-security-inc/design-system-loaders'
import { css } from '@emotion/css'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Text } from '@contrast-security-inc/design-system-typography'
import Markdown from 'react-markdown'
import { useCurrentOrganization, useCurrentUser } from 'contrastBase/state'
import { useCreateRemediationGuidance } from './api/use-create-remediation-guidance'
import {
  GuidanceStatusType,
  useFetchRemediationGuidanceStatus
} from './api/use-fetch-remediation-guidance-status'
import { useFetchRemediationGuidanceResult } from './api/use-fetch-remediation-guidance-result'
import { useCreateRemediationGuidanceFeedback } from './api/use-create-remediation-guidance-feedback'
import { useFetchAiSettings } from '../general-organization-settings/ai-ml/api/use-fetch-ai-settings'
import { formatStyles } from '../react-intl-helpers'
import { ANTHROPIC_TERMS_LINK } from '../general-organization-settings/ai-ml/aiml-organization-settings'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'

/* Process flow:
1. User clicks on the "Use Contrast AI" button and we send the inputs to request a remediationGuidanceId
2. Display a loading message until we get the id or an error
3. When we have the id, we poll the status until it's ready
4. When it's ready, we request the guidance
5. Display the guidance when it is ready

But, our miscellaneous checks didn't like me calling the custom query hooks in a conditional manner; so, instead of having
a state machine implemented with an if-else block, I had to nest the different states in functional components like a
Matroska doll. This is a bit more verbose than the original implementation, but it should work just as well.

The nesting order is AiHowToFix -> GuidanceTicket -> Status -> RemediationGuidance (the feedback is handled via a
useEffect() in RemediationGuidance).
 */

const aiButtonCss = css`
  background-color: #7d7cf9;
  background-image: linear-gradient(#c621e5, #7d7cf9);
  color: ${theme.color.white};

  span > svg {
    fill: ${theme.color.white};
  }
`

const recommendationCss = css`
  margin-top: 16px;
  padding: 24px;
  color: ${theme.color.neutral90};
  border-radius: 4px;
  border: 2px solid #0000;
  background: linear-gradient(to right, #fdf5ff, #fdf5ff) padding-box,
    linear-gradient(to right, #c621e5, #7d7cf9) border-box;
`

export type FeedbackType = 'Positive' | 'Negative' | 'Deselected'

interface RemediationGuidanceProps {
  organizationId: string
  applicationId: string
  remediationGuidanceId: string
  status: GuidanceStatusType
}

function RemediationGuidance({
  organizationId,
  applicationId,
  remediationGuidanceId,
  status
}: RemediationGuidanceProps) {
  const intl = useIntl()
  const [feedback, setFeedback] = useState<FeedbackType>()

  useCreateRemediationGuidanceFeedback({
    organizationId,
    applicationId,
    remediationGuidanceId,
    feedback,
    enabled: feedback !== undefined
  })

  const { isLoading, error, data } = useFetchRemediationGuidanceResult({
    organizationId,
    applicationId,
    remediationGuidanceId,
    doRefetch: status === 'partial',
    enabled: status !== 'pending'
  })
  if (error) {
    return <AiError />
  } else if (!isLoading && data) {
    const sanitizeMarkdown = (input: string) => {
      input = input.replaceAll(/\\n/g, '\n')
      input = input.replaceAll(/\\t/g, '\t')
      input = input.replaceAll(/\\r/g, '\r')
      return input
    }

    // display guidance
    return (
      <Stack flexDirection="column" spacing="40px">
        <div>
          <Markdown>{sanitizeMarkdown(data?.guidance ?? '')}</Markdown>
        </div>
        <Stack alignItems="center" spacing="16px">
          <Text color="neutral70">
            {intl.formatMessage({ id: 'detail_aiHowToFix_wasThisHelpful' })}
          </Text>
          <Stack alignItems="center" spacing="4px">
            <IconButton
              Icon={IconNegativeResults}
              data-e2e="ai-how-to-fix-thumbs-down"
              testId="ai-how-to-fix-thumbs-down-test-id"
              aria-label="give recommendation a thumbs-down"
              onClick={() => {
                if (feedback === 'Negative') {
                  setFeedback('Deselected')
                } else {
                  setFeedback('Negative')
                }
              }}
              className={css`
                svg {
                  fill: ${feedback === 'Negative'
                    ? theme.color.purple90
                    : theme.color.neutral60};
                }
              `}
            />
            <IconButton
              Icon={IconPositiveResults}
              data-e2e="ai-how-to-fix-thumbs-up"
              testId="ai-how-to-fix-thumbs-up-test-id"
              aria-label="give recommendation a thumbs-up"
              onClick={() => {
                if (feedback === 'Positive') {
                  setFeedback('Deselected')
                } else {
                  setFeedback('Positive')
                }
              }}
              className={css`
                svg {
                  fill: ${feedback === 'Positive'
                    ? theme.color.purple90
                    : theme.color.neutral60};
                }
              `}
            />
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return <AiWaiting />
}

interface StatusProps {
  organizationId: string
  applicationId: string
  remediationGuidanceId: string
}

function Status({
  organizationId,
  applicationId,
  remediationGuidanceId
}: StatusProps) {
  const [isStatusReady, setIsStatusReady] = useState(false)

  //poll to see if remediation guidance is ready
  const { isLoading, error, data } = useFetchRemediationGuidanceStatus({
    organizationId,
    applicationId,
    remediationGuidanceId,
    enabled: !isStatusReady
  })
  if (error) {
    return <AiError />
  }
  if (!isLoading && data?.status !== 'pending') {
    if (data?.status === 'ready' && !isStatusReady) {
      // We don't need to continue querying for partial responses, the backend has received the full LLM response
      setIsStatusReady(true)
    }

    // We have either a partial or fully ready response to display to the user
    return (
      <RemediationGuidance
        organizationId={organizationId}
        applicationId={applicationId}
        remediationGuidanceId={remediationGuidanceId}
        status={data?.status ?? 'pending'}
      />
    )
  }

  return <AiWaiting />
}

interface GuidanceTicketProps {
  organizationId: string
  applicationId: string
  language: string
  vulnerabilityId: string
}

function GuidanceTicket({
  organizationId,
  applicationId,
  vulnerabilityId,
  language
}: GuidanceTicketProps) {
  //ask for remediation guidance id
  const { isLoading, error, data } = useCreateRemediationGuidance({
    organizationId,
    applicationId,
    vulnerabilityId,
    language
  })
  if (error) {
    return <AiError />
  }
  if (!isLoading && data) {
    return (
      <Status
        organizationId={organizationId}
        applicationId={applicationId}
        remediationGuidanceId={data.remediationGuidanceId}
      />
    )
  }

  return <AiWaiting />
}

export function AiError() {
  const intl = useIntl()

  return <Text>{intl.formatMessage({ id: 'detail_aiHowToFix_error' })}</Text>
}

export function AiWaiting() {
  const intl = useIntl()

  return (
    <Stack
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      spacing="8px"
    >
      <Spinner size={32} />
      <Text>{intl.formatMessage({ id: 'detail_aiHowToFix_generating' })}</Text>
    </Stack>
  )
}

export interface AiHowToFixProps {
  applicationId: string
  vulnerabilityId: string
}

export function AiHowToFix({
  applicationId,
  vulnerabilityId
}: AiHowToFixProps) {
  const intl = useIntl()

  const [showRecommendation, setShowRecommendation] = useState(false)

  const currentOrg = useCurrentOrganization()
  const currentUser = useCurrentUser()
  const inputs = {
    organizationId: currentOrg.currentOrganization.organizationUuid,
    applicationId,
    vulnerabilityId,
    language: currentUser.currentUser.locale || 'en'
  }

  let showAiHowToFix = false
  let showDisclaimer = false
  const { isLoading, error, data } = useFetchAiSettings({
    organizationId: currentOrg.currentOrganization.organizationUuid,
    enabled: true
  })
  if (error) {
    return <AiError />
  }
  if (!isLoading && data) {
    showAiHowToFix = data.settings.intelligent_remediation_guidance_enabled

    showDisclaimer =
      data.settings.intelligent_remediation_guidance_disclaimer_enabled
  }

  return (
    <div
      className={css`
        margin-bottom: 48px;
      `}
    >
      <Stack flexDirection="column">
        <Stack alignItems="center" spacing="8px">
          <Tooltip
            body={intl.formatMessage({ id: 'detail_aiHowToFix_enable_tip' })}
            offset={[0, 8]}
            placement="top"
            title=""
            variant="regular"
            enabled={!showAiHowToFix && !showRecommendation}
          >
            <Button
              data-e2e="ai-how-to-fix-button"
              testId="ai-how-to-fix-button-test-id"
              onClick={() => setShowRecommendation(true)}
              isDisabled={!showAiHowToFix || showRecommendation}
              className={aiButtonCss}
              Icon={IconAi}
              aria-label={'Use Contrast AI'}
            >
              {intl.formatMessage({ id: 'action_aiHowToFix_startAi' })}
            </Button>
          </Tooltip>
          <div
            className={css`
              margin-left: 8px;
              a,
              a:link,
              a:visited,
              a:focus {
                color: #c621e5;
              }
            `}
          >
            <a
              href="https://docs.contrastsecurity.com/en/contrast-ai.html"
              target="_blank"
            >
              {intl.formatMessage({ id: 'detail_aiHowToFix_learnMore' })}
            </a>
          </div>
        </Stack>
        {showDisclaimer && (
          <Stack>
            <div
              className={css`
                color: #c621e5;
              `}
            >
              {intl.formatMessage(
                {
                  id: 'detail_intelligentRemediationGuidanceDisclaimer'
                },
                {
                  ...formatStyles.link({
                    href: ANTHROPIC_TERMS_LINK,
                    isExternal: true
                  })
                }
              )}
            </div>
          </Stack>
        )}
      </Stack>
      {showRecommendation && (
        <Stack
          className={recommendationCss}
          data-e2e="ai-how-to-fix-recommendation"
          testId="ai-how-to-fix-recommendation-id"
          flexDirection="column"
          spacing="40px"
        >
          <GuidanceTicket {...inputs} />
        </Stack>
      )}
      <br />
    </div>
  )
}
